import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { eq } from 'tio-ui/utilities';
import t from 'ember-intl/helpers/t';
import { Section } from 'tio-ui/components/layout';
import Table from 'tio-ui/components/table';
import Popover from 'tio-ui/components/popover';
import { PhInfo } from 'ember-phosphor-icons';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';

const PSLF_0726_APP = 'PSLF-0726';

// The legacy PSLF estimator results live on this route; history rows for old
// "PSLF" runs link there so the user can revisit them too.
const LEGACY_PSLF_RESULTS_ROUTE = 'authenticated.pslf.dashboard.forgiveness-estimator.results';

interface UsageHistorySignature {
  Args: {
    recommendations: RecommendationModel[];
  };
  Element: HTMLElement;
}

const UsageHistory: TOC<UsageHistorySignature> = <template>
  <Section data-test-pslf-0726-history ...attributes>
    <:header>{{t "forgiveness_estimator_0726.history.title"}}</:header>
    <:body>
      <Table @isLoading={{false}}>
        <:thead as |options|>
          <options.tr>
            <options.th>{{t "forgiveness_estimator_0726.history.date"}}</options.th>
            <options.th>{{t "forgiveness_estimator_0726.history.tool"}}</options.th>
            <options.th>{{t "forgiveness_estimator_0726.history.view"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each @recommendations as |rec|}}
            <options.tr data-test-pslf-0726-history-row={{rec.requestingApp}}>
              <options.td>
                {{safeFormatDate rec.createdAt month="short" day="numeric" year="numeric"}}
              </options.td>
              <options.td>
                {{#if (eq rec.requestingApp PSLF_0726_APP)}}
                  {{t "forgiveness_estimator_0726.history.tool_pslf_0726"}}
                {{else}}
                  <span class="tio-row gap-1 items-center">
                    {{t "forgiveness_estimator_0726.history.tool_pslf_legacy"}}
                    <Popover @triggerMode="hover">
                      <:trigger as |pop|>
                        <span
                          tabindex="0"
                          {{pop.register}}
                          {{pop.hoverHandlers}}
                          data-test-pslf-0726-history-info
                        >
                          <PhInfo class="tio-fg-secondary" @size={{16}} />
                        </span>
                      </:trigger>
                      <:body>
                        <p class="text-sm max-w-xs">
                          {{t "forgiveness_estimator_0726.history.legacy_info"}}
                        </p>
                      </:body>
                    </Popover>
                  </span>
                {{/if}}
              </options.td>
              <options.td>
                {{#if (eq rec.requestingApp PSLF_0726_APP)}}
                  <LinkTo
                    @route="authenticated.pslf.dashboard.forgiveness-estimator-0726.results"
                    @model={{rec.id}}
                    class="tio-copy"
                  >
                    {{t "forgiveness_estimator_0726.history.view"}}
                  </LinkTo>
                {{else}}
                  <LinkTo @route={{LEGACY_PSLF_RESULTS_ROUTE}} @model={{rec.id}} class="tio-copy">
                    {{t "forgiveness_estimator_0726.history.view"}}
                  </LinkTo>
                {{/if}}
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
      </Table>
    </:body>
  </Section>
</template>;

export default UsageHistory;
